import styled from '@emotion/styled';
import { Modal } from '@travelnest/merlin.modal';
import * as React from 'react';

import { Spacer } from '@travelnest/merlin.spacer';
import { HeadingSmall, Paragraph } from '@travelnest/merlin.typography';

import { Button, PresetThemes } from '@travelnest/merlin.button';
import theme from '@travelnest/merlin.theme';

interface IHeaderImageProps {
	src: string;
	tint: string;
}

const HeaderImage = styled.img<IHeaderImageProps>`
	height: 140px;
	width: 100%;
	@media only screen and (min-width: 768px) {
		height: 200px;
	}

	background-color: ${({ tint }) => tint};
`;

const LayoutWrapper = styled.div`
	padding: ${theme.layout.spacing.standard};
`;

const PageOne = () => <>
	<HeaderImage src="../../../71eb904/assets/icons/calendar/calendarone.svg" tint={theme.colours.primary.tint} />
	<LayoutWrapper>
		<HeadingSmall>Keep your calendar up to date</HeadingSmall>
		<Spacer spacing="small" />
		<Paragraph>
			Once you have synced all your calendars you must continue to keep your Travelnest calendar up to date to avoid double bookings.
		</Paragraph>
	</LayoutWrapper>
</>;

const PageTwo = () => <>
	<HeaderImage src="../../../71eb904/assets/icons/calendar/calendartwo.svg" tint={theme.colours.error.tint} />
	<LayoutWrapper>
		<HeadingSmall>View synced dates</HeadingSmall>
		<Spacer spacing="small" />
		<Paragraph>
			When we receive blocked dates via calendar sync from another source (e.g. a direct listing), these are shown in blue on your Travelnest calendar.
		</Paragraph>
	</LayoutWrapper>
</>;

const PageThree = () => <>
	<HeaderImage src="../../../71eb904/assets/icons/calendar/calendarthree.svg" tint={theme.colours.success.tint} />
	<LayoutWrapper>
		<HeadingSmall>Overwrite existing blocked dates</HeadingSmall>
		<Spacer spacing="small" />
		<Paragraph>
			If you need to block out a longer date range, you can overwrite existing blocked dates.
			This will fill in all the gaps around any existing blocked dates.
		</Paragraph>
	</LayoutWrapper>
</>;

const modalPages = {
	1: <PageOne />,
	2: <PageTwo />,
	3: <PageThree />,
};

const buildModalButtons = (
	page: number,
	next: () => any,
	prev: () => any,
	close: () => any,
) => {
	const btnNext = (
		<Button
			buttonType={PresetThemes.Primary}
			onClick={next}
		>
			Next
		</Button>
	);
	const btnPrev = <Button onClick={prev}>Previous</Button>;
	const btnFinish = (
		<Button
			buttonType={PresetThemes.Primary}
			onClick={close}
		>
			Finish
		</Button>
	);

	if (page === 1) {
		return { actionRHS: btnNext };
	} else if (page === 3) {
		return { actionRHS: btnFinish };
	} else {
		return { actionLHS: btnPrev, actionRHS: btnNext };
	}
};

interface ICalendarEducationProps {
	onCloseModal(): void;
}

export const CalendarEducationModal: React.FC<ICalendarEducationProps> = (props) => {
	const { onCloseModal } = props;

	const [currentPage, setCurrentPage] = React.useState<number>(1);
	const onNextPageTap = () => {
		if (currentPage !== 3) {
			setCurrentPage(currentPage + 1);
		}
	};

	const onPrevPageTap = () => {
		if (currentPage !== 1) {
			setCurrentPage(currentPage - 1);
		}
	};
	const page = modalPages[currentPage];
	const buttons = buildModalButtons(currentPage, onNextPageTap, onPrevPageTap, props.onCloseModal);

	return (
		<Modal
			extraClasses="calendar-education-modal"
			{...buttons}
			title={`${currentPage}/${Object.keys(modalPages).length}`}
			onRequestClose={onCloseModal}
			shouldCloseOnOverlayClick={false}
		>
			{page}
		</Modal>
	);
};
